// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes for each scene
export const SCENE_COLORS = {
  scene1: { primaryColor: '#6B7280', accentColor: '#EF4444' },   // Gray/Red - Muted, concerning
  scene2: { primaryColor: '#22C55E', accentColor: '#ffffff' },   // Green/White - Energetic
  scene3: { primaryColor: '#ffffff', accentColor: '#F59E0B' },   // White/Amber - Professional
  scene4: { primaryColor: '#F59E0B', accentColor: '#ffffff' },   // Amber/White - Urgency
  scene5: { primaryColor: '#EF4444', accentColor: '#ffffff' },   // Red/White - Dramatic
  scene6: { primaryColor: '#3B82F6', accentColor: '#FCD34D' },   // Blue/Gold - Professional
  scene7: { primaryColor: '#FCD34D', accentColor: '#ffffff' },   // Gold/White - Premium
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },     // White/Gold - Brand
};

// Background images for each scene
export const SCENE_IMAGES = {
  scene1: 'images/gdm-quiet-phone/scene1-quiet-phone.png',
  scene2: 'images/gdm-quiet-phone/scene2-ringing-phone.png',
  scene3: 'images/gdm-quiet-phone/scene3-neighborhood.png',
  scene4: 'images/gdm-quiet-phone/scene4-technician.png',
  scene5: 'images/gdm-quiet-phone/scene5-search-results.png',
  scene6: 'images/gdm-quiet-phone/scene6-analytics.png',
  scene7: 'images/gdm-quiet-phone/scene7-report.png',
};

// Hg market report presentation slides (captured from reports.garagedoormarketers.com)
export const REPORT_SLIDES = [
  'images/gdm-quiet-phone/report-slide-01.png',
  'images/gdm-quiet-phone/report-slide-05.png',
  'images/gdm-quiet-phone/report-slide-07.png',
  'images/gdm-quiet-phone/report-slide-12.png',
];

// Word timings extracted from audio using Whisper (incredibly-fast-whisper)
// Voice: Mark (UgBBYS2sOqTuMpoF3BR0) - American male
// Manual corrections: punctuation + same-day merge
export const WORD_TIMINGS: WordTiming[] = [
  { text: 'Your', startMs: 0, endMs: 120 },
  { text: 'phone.', startMs: 120, endMs: 520 },
  { text: 'Quiet.', startMs: 520, endMs: 1520 },
  { text: 'Your', startMs: 1520, endMs: 2380 },
  { text: 'competitor\'s', startMs: 2380, endMs: 2900 },
  { text: 'phone.', startMs: 2900, endMs: 3260 },
  { text: 'Ringing.', startMs: 3260, endMs: 4140 },
  { text: 'Same', startMs: 4140, endMs: 5100 },
  { text: 'city.', startMs: 5100, endMs: 5520 },
  { text: 'Same', startMs: 5520, endMs: 5880 },
  { text: 'services.', startMs: 5880, endMs: 6600 },
  { text: 'Different', startMs: 6600, endMs: 7300 },
  { text: 'results.', startMs: 7300, endMs: 8080 },
  { text: 'They\'re', startMs: 8080, endMs: 8580 },
  { text: 'getting', startMs: 8580, endMs: 8800 },
  { text: 'the', startMs: 8800, endMs: 8940 },
  { text: 'emergency', startMs: 8940, endMs: 9240 },
  { text: 'repairs.', startMs: 9240, endMs: 9980 },
  { text: 'The', startMs: 10580, endMs: 10760 },
  { text: 'commercial', startMs: 10760, endMs: 11080 },
  { text: 'installs.', startMs: 11080, endMs: 11800 },
  { text: 'The', startMs: 11800, endMs: 12420 },
  { text: 'same-day', startMs: 12420, endMs: 12700 },
  { text: 'jobs.', startMs: 12700, endMs: 13380 },
  { text: 'Why?', startMs: 13380, endMs: 14740 },
  { text: 'They', startMs: 15740, endMs: 16700 },
  { text: 'own', startMs: 16700, endMs: 16940 },
  { text: 'the', startMs: 16940, endMs: 17080 },
  { text: 'search', startMs: 17080, endMs: 17300 },
  { text: 'terms', startMs: 17300, endMs: 17640 },
  { text: 'you', startMs: 17640, endMs: 17840 },
  { text: 'don\'t.', startMs: 17840, endMs: 18180 },
  { text: 'We', startMs: 18180, endMs: 18720 },
  { text: 'map', startMs: 18720, endMs: 18960 },
  { text: 'your', startMs: 18960, endMs: 19160 },
  { text: 'entire', startMs: 19160, endMs: 19400 },
  { text: 'market.', startMs: 19400, endMs: 20100 },
  { text: 'Who', startMs: 20100, endMs: 20720 },
  { text: 'ranks', startMs: 20720, endMs: 21040 },
  { text: 'above', startMs: 21040, endMs: 21280 },
  { text: 'you.', startMs: 21280, endMs: 21640 },
  { text: 'What\'s', startMs: 21640, endMs: 21960 },
  { text: 'working.', startMs: 21960, endMs: 22440 },
  { text: 'What\'s', startMs: 22440, endMs: 22760 },
  { text: 'not.', startMs: 22760, endMs: 23240 },
  { text: 'Free', startMs: 23240, endMs: 24380 },
  { text: 'Market', startMs: 24380, endMs: 24700 },
  { text: 'Report.', startMs: 24700, endMs: 25280 },
  { text: 'Grab', startMs: 25280, endMs: 25720 },
  { text: 'yours', startMs: 25720, endMs: 25960 },
  { text: 'now.', startMs: 25960, endMs: 27100 },
];

// Scene definitions with word groupings (aligned to Whisper timing)
export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Hook',
    startMs: 0,
    endMs: 1520, // Before "Your competitor's"
    words: WORD_TIMINGS.slice(0, 3), // "Your phone. Quiet."
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Contrast',
    startMs: 1520,
    endMs: 4140, // Before "Same city"
    words: WORD_TIMINGS.slice(3, 7), // "Your competitor's phone. Ringing."
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Setup',
    startMs: 4140,
    endMs: 8080, // Before "They're getting"
    words: WORD_TIMINGS.slice(7, 13), // "Same city. Same services. Different results."
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 4,
    name: 'Pain',
    startMs: 8080,
    endMs: 13380, // Before "Why?"
    words: WORD_TIMINGS.slice(13, 24), // "They're getting... same-day jobs."
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomInLower,
  },
  {
    id: 5,
    name: 'Question',
    startMs: 13380,
    endMs: 18180, // Before "We map"
    words: WORD_TIMINGS.slice(24, 32), // "Why? They own the search terms you don't."
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 6,
    name: 'Solution',
    startMs: 18180,
    endMs: 23240, // Before "Free Market Report"
    words: WORD_TIMINGS.slice(32, 45), // "We map your entire market... What's not."
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 7,
    name: 'CTA',
    startMs: 23240,
    endMs: 27100, // End of voiceover + linger
    words: WORD_TIMINGS.slice(45), // "Free Market Report. Grab yours now."
    ...SCENE_COLORS.scene7,
    backgroundImage: SCENE_IMAGES.scene7,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

// Logo scene (after voiceover) - full animated logo playback
export const LOGO_SCENE = {
  startMs: 27100,
  endMs: 32200,
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
